import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { ApiError } from "@/lib/api";
import { formatNumber, formatPoMoneyWithSymbol } from "@/lib/mrp";
import {
  fetchMrpRfqsBySku,
  MRP_RFQ_STATUS_HOVER_HINT,
  MRP_RFQ_STATUS_LABEL,
  mrpRfqStatusBadgeTone,
  type MrpRfqSkuRelatedRow,
  type MrpRfqStatus,
} from "@/lib/rfqApi";
import { HoverHintWithGlyph } from "@/components/ui/title-help";

const SKU_RELATED_QUOTES_STALE_MS = 30_000;

const COLUMNS: HubDataTableColumn<MrpRfqSkuRelatedRow>[] = [
  {
    id: "rfq",
    header: "RFQ",
    overflow: "nowrap",
    sortValue: (row) => row.rfq_number,
    cell: (row) => (
      <Link
        to={`/mrp/rfqs/${row.rfq_id}`}
        className="font-mono text-xs text-brand-700 hover:text-brand-900 hover:underline"
      >
        {row.rfq_number}
      </Link>
    ),
  },
  {
    id: "supplier",
    header: "Supplier",
    sortValue: (row) => row.supplier_name ?? "",
    cell: (row) => row.supplier_name ?? "—",
  },
  {
    id: "quoteDate",
    header: "Quote date",
    overflow: "nowrap",
    sortValue: (row) => row.quoted_at ?? "",
    cell: (row) => (row.quoted_at != null && row.quoted_at !== "" ? row.quoted_at.slice(0, 10) : "—"),
  },
  {
    id: "qtyQuoted",
    header: "Qty quoted",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.quoted_quantity,
    cell: (row) => formatNumber(row.quoted_quantity),
  },
  {
    id: "quotedPrice",
    header: "Quoted price",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.quoted_unit_price ?? "",
    cell: (row) =>
      row.quoted_unit_price == null ? "—" : formatPoMoneyWithSymbol(row.quoted_unit_price, row.currency),
  },
  {
    id: "leadTime",
    header: "Lead time (wks)",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.quoted_lead_time_weeks ?? -1,
    cell: (row) => (row.quoted_lead_time_weeks == null ? "—" : row.quoted_lead_time_weeks),
  },
  {
    id: "expires",
    header: "Expires",
    overflow: "nowrap",
    sortValue: (row) => row.quote_valid_until ?? "",
    cell: (row) => row.quote_valid_until ?? "—",
  },
  {
    id: "status",
    header: (
      <span className="inline-flex items-center gap-0.5">
        Status
        <HoverHintWithGlyph hoverHint={MRP_RFQ_STATUS_HOVER_HINT} />
      </span>
    ),
    sortKey: "status",
    overflow: "nowrap",
    sortValue: (row) => row.status,
    cell: (row) => (
      <Badge tone={mrpRfqStatusBadgeTone(row.status as MrpRfqStatus)}>
        {MRP_RFQ_STATUS_LABEL[row.status as MrpRfqStatus]}
      </Badge>
    ),
  },
];

export function SkuRelatedQuotesTab(props: { sku: string }): JSX.Element {
  const quotesQuery = useQuery<MrpRfqSkuRelatedRow[], ApiError>({
    queryKey: ["mrp", "rfqs", "by-sku", props.sku],
    queryFn: () => fetchMrpRfqsBySku(props.sku),
    enabled: props.sku.length > 0,
    staleTime: SKU_RELATED_QUOTES_STALE_MS,
    refetchOnWindowFocus: false,
  });

  return (
    <HubDataTable
      title="Related quotes"
      columns={COLUMNS}
      rows={quotesQuery.data ?? []}
      rowKey={(row) => `${row.rfq_id}-${row.rfq_item_id}-${row.quote_id ?? "none"}`}
      fillViewport={false}
      loading={quotesQuery.isLoading}
      loadingMessage="Loading related quotes…"
      error={quotesQuery.error ? quotesQuery.error.message : null}
      empty="No RFQs or supplier quotes for this SKU yet."
    />
  );
}
